import { useCallback, useEffect, useState } from 'react';

import { useGetLeadsKanbanQuery, useGetLeadsKanbanStatusCountsQuery } from '../services/api/leads.api';
import type { ExtendedLead } from '../services/types/lead';

/**
 * Custom hook for managing Kanban board pagination with temporal filters,
 * virtualization support, and skeleton loading states
 *
 * @param {number | null} businessUnitId - The business unit ID to filter leads by
 * @param {Object} [options] - Pagination options
 * @param {string} [options.startDate] - ISO date string for range start (caller-owned)
 * @param {string} [options.endDate] - ISO date string for range end (caller-owned)
 * @param {string} [options.filterModel] - Serialized GridFilterModel
 * @returns {Object} Object containing pagination state, handlers and query data
 */
const useKanbanPagination = (
  businessUnitId: number | null,
  options?: {
    startDate?: string | null;
    endDate?: string | null;
    filterModel?: string;
    searchTerm?: string;
    skip?: boolean;
  }
) => {
  const { startDate, endDate, filterModel, searchTerm, skip = false } = options ?? {};

  const [statusPages, setStatusPages] = useState<Record<string, number>>({
    CREATED: 0,
    HANDLED_BY_AI: 0,
    INTERESTING_FOR_SALESMAN: 0,
    UPLOADED_TO_CRM: 0,
    PENDING_TO_BE_HANDLED: 0,
    PENDING_TO_BE_ASSIGNED: 0,
    ASSIGNED_TO_SALESMAN: 0,
    HANDLED_BY_SALESMAN: 0,
    CLOSED: 0,
  });

  const [loadingStates, setLoadingStates] = useState<
    Record<
      string,
      {
        isLoadingMore: boolean;
        isRefreshing: boolean;
      }
    >
  >({
    CREATED: { isLoadingMore: false, isRefreshing: false },
    HANDLED_BY_AI: { isLoadingMore: false, isRefreshing: false },
    INTERESTING_FOR_SALESMAN: { isLoadingMore: false, isRefreshing: false },
    UPLOADED_TO_CRM: { isLoadingMore: false, isRefreshing: false },
    PENDING_TO_BE_HANDLED: { isLoadingMore: false, isRefreshing: false },
    PENDING_TO_BE_ASSIGNED: { isLoadingMore: false, isRefreshing: false },
    ASSIGNED_TO_SALESMAN: { isLoadingMore: false, isRefreshing: false },
    HANDLED_BY_SALESMAN: { isLoadingMore: false, isRefreshing: false },
    CLOSED: { isLoadingMore: false, isRefreshing: false },
  });

  const pageSize = 20;

  const queryParams = {
    businessUnitId: businessUnitId || 0,
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
    pageSize,
    statusPages,
    ...(filterModel ? { filterModel } : {}),
    ...(searchTerm ? { searchTerm } : {}),
  };

  const {
    data: kanbanData,
    isLoading: isInitialLoading,
    isFetching,
    refetch: refetchKanban,
  } = useGetLeadsKanbanQuery(queryParams, {
    skip: skip || !businessUnitId,
  });

  const {
    data: statusCountsData,
    isLoading: isStatusCountsLoading,
    refetch: refetchStatusCounts,
  } = useGetLeadsKanbanStatusCountsQuery(
    {
      businessUnitId: businessUnitId || 0,
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
      ...(filterModel ? { filterModel } : {}),
      ...(searchTerm ? { searchTerm } : {}),
    },
    {
      skip: skip || !businessUnitId,
    }
  );

  const [accumulatedLeads, setAccumulatedLeads] = useState<Record<string, ExtendedLead[]>>({});

  const loadMoreLeads = useCallback(
    (status: string) => {
      if (!kanbanData?.paginationInfo[status]?.hasMore || loadingStates[status].isLoadingMore) {
        return;
      }

      setLoadingStates((prev) => ({
        ...prev,
        [status]: { ...prev[status], isLoadingMore: true },
      }));

      const nextPage = (statusPages[status] || 0) + 1;
      setStatusPages((prev) => ({
        ...prev,
        [status]: nextPage,
      }));
    },
    [kanbanData, loadingStates, statusPages]
  );

  const refreshAllLeads = useCallback(() => {
    setStatusPages({
      CREATED: 0,
      HANDLED_BY_AI: 0,
      INTERESTING_FOR_SALESMAN: 0,
      UPLOADED_TO_CRM: 0,
      PENDING_TO_BE_HANDLED: 0,
      PENDING_TO_BE_ASSIGNED: 0,
      ASSIGNED_TO_SALESMAN: 0,
      HANDLED_BY_SALESMAN: 0,
      CLOSED: 0,
    });
    setAccumulatedLeads({});
    setLoadingStates((prev) =>
      Object.keys(prev).reduce(
        (acc, status) => ({
          ...acc,
          [status]: { isLoadingMore: false, isRefreshing: true },
        }),
        {}
      )
    );
    if (businessUnitId) {
      refetchKanban();
      refetchStatusCounts();
    }
  }, [businessUnitId, refetchKanban, refetchStatusCounts]);

  // Reset pagination + accumulated leads when filters or businessUnit change.
  // RTK Query auto-refetches when args change, so we only reset local state here.
  useEffect(() => {
    setStatusPages({
      CREATED: 0,
      HANDLED_BY_AI: 0,
      INTERESTING_FOR_SALESMAN: 0,
      UPLOADED_TO_CRM: 0,
      PENDING_TO_BE_HANDLED: 0,
      PENDING_TO_BE_ASSIGNED: 0,
      ASSIGNED_TO_SALESMAN: 0,
      HANDLED_BY_SALESMAN: 0,
      CLOSED: 0,
    });
    setAccumulatedLeads({});
  }, [businessUnitId, startDate, endDate, filterModel, searchTerm]);

  useEffect(() => {
    if (!isFetching && kanbanData) {
      setLoadingStates((prev) =>
        Object.keys(prev).reduce(
          (acc, status) => ({
            ...acc,
            [status]: { isLoadingMore: false, isRefreshing: false },
          }),
          {}
        )
      );

      setAccumulatedLeads((prevAccumulated) => {
        const merged = { ...prevAccumulated };

        Object.entries(kanbanData.leadsByStatus).forEach(([status, newLeads]) => {
          const currentPage = statusPages[status];
          const existing = merged[status] || [];

          if (currentPage === 0) {
            merged[status] = newLeads as ExtendedLead[];
          } else {
            const existingIds = new Set(existing.map((lead) => lead.id));
            const uniqueNewLeads = (newLeads as ExtendedLead[]).filter((lead) => !existingIds.has(lead.id));
            merged[status] = [...existing, ...uniqueNewLeads];
          }
        });

        return merged;
      });
    }
  }, [isFetching, kanbanData, statusPages]);

  return {
    leadsByStatus: accumulatedLeads,
    statusCounts: statusCountsData?.statusCounts || {},
    paginationInfo: kanbanData?.paginationInfo || {},
    totalLeads: statusCountsData?.totalLeads || 0,
    isInitialLoading: isInitialLoading || isStatusCountsLoading,
    isFetching,
    loadingStates,
    pageSize,
    statusPages,
    loadMoreLeads,
    refreshAllLeads,
    getSkeletonCount: (status: string) => {
      const totalCount = kanbanData?.paginationInfo[status]?.totalCount || 0;
      const currentLeads = accumulatedLeads[status]?.length || 0;
      return Math.min(Math.max(3, Math.ceil((totalCount - currentLeads) / 4)), 8);
    },
    hasMoreLeads: (status: string) => kanbanData?.paginationInfo[status]?.hasMore || false,
  };
};

export default useKanbanPagination;
